/**
 * 🎨 Visual Theme Selector Component
 * Selector visual elegante para cambiar temas de la aplicación
 * 
 * @description Componente que permite seleccionar entre diferentes temas
 * con previsualizaciones visuales y transiciones suaves
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Stack,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Accessibility as AccessibilityIcon,
  Business as BusinessIcon,
  AutoAwesome as ModernIcon,
  SettingsBrightness as AutoIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import { useTheme } from '../../context/ThemeContext';
import { themeCategories } from '../../themes';

const ThemeSelector = ({ open, onClose }) => {
  const { activeTheme, availableThemes, switchTheme, colorSchemeMode, changeColorSchemeMode } = useTheme();
  const isMobile = useMediaQuery('(max-width: 599px)');
  const [hoveredTheme, setHoveredTheme] = useState(null);

  // Iconos para categorías
  const categoryIcons = {
    modern: <ModernIcon />,
    business: <BusinessIcon />,
    accessibility: <AccessibilityIcon />
  };

  // Obtener temas por categoría (excluyendo ocultos)
  const getThemesByCategory = (category) => {
    return Object.values(availableThemes).filter(theme =>
      theme.category === category && !theme.hidden
    );
  };

  // Manejar selección de tema
  const handleThemeSelect = (themeId) => {
    switchTheme(themeId);
    // Pequeña pausa para mostrar la transición
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // Renderizar preview de colores
  const renderColorPreview = (theme) => (
    <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          backgroundColor: theme.preview.primary,
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      />
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          backgroundColor: theme.preview.secondary,
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      />
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          backgroundColor: theme.preview.background,
          border: '1px solid rgba(0, 0, 0, 0.1)'
        }}
      />
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          backgroundColor: theme.preview.surface,
          border: '1px solid rgba(0, 0, 0, 0.1)'
        }}
      />
    </Stack>
  );

  // Renderizar card de tema
  const renderThemeCard = (theme) => {
    const isActive = activeTheme === theme.id;
    const isHovered = hoveredTheme === theme.id;

    return (
      <Card
        key={theme.id}
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
          boxShadow: isHovered ? 4 : 1,
          border: isActive ? 2 : 1,
          borderColor: isActive ? 'primary.main' : 'divider',
          position: 'relative',
          '&:hover': {
            borderColor: 'primary.main'
          }
        }}
        onMouseEnter={() => setHoveredTheme(theme.id)}
        onMouseLeave={() => setHoveredTheme(null)}
        onClick={() => handleThemeSelect(theme.id)}
      >
        {/* Indicador de tema activo */}
        {isActive && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1
            }}
          >
            <Avatar
              sx={{
                width: 24,
                height: 24,
                bgcolor: 'primary.main',
                color: 'primary.contrastText'
              }}
            >
              <CheckIcon sx={{ fontSize: 16 }} />
            </Avatar>
          </Box>
        )}

        <CardContent sx={{ p: 2, minHeight: 200, display: 'flex', flexDirection: 'column' }}>
          {/* Header con nombre y badges */}
          <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {theme.displayName}
              </Typography>
              {theme.accessibility && (
                <Chip
                  icon={<AccessibilityIcon />}
                  label="A11y"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1,
                minHeight: '2.5em', // Altura mínima para 2 líneas de texto
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {theme.description}
            </Typography>
          </Box>

          {/* Preview de colores */}
          {renderColorPreview(theme)}

          {/* Información adicional */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
            <Chip
              label={theme.mode === 'dark' ? '🌙 Oscuro' : '☀️ Claro'}
              size="small"
              variant="outlined"
            />
            
            <Tooltip title="Vista previa">
              <IconButton size="small" color="primary">
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '70vh'
        }
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <PaletteIcon color="primary" />
            <Typography variant="h5" fontWeight={600}>
              Selector de Temas
            </Typography>
          </Stack>
          
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Personaliza la apariencia de toda la aplicación con estos temas cuidadosamente diseñados
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        {/* Color scheme mode picker */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Esquema de color
          </Typography>
          <ToggleButtonGroup
            value={colorSchemeMode}
            exclusive
            onChange={(_, val) => { if (val) changeColorSchemeMode(val); }}
            size="small"
            fullWidth={isMobile}
          >
            <ToggleButton value="auto" aria-label="Sistema (Auto)">
              <AutoIcon fontSize="small" sx={{ mr: 0.75 }} />
              Sistema
            </ToggleButton>
            <ToggleButton value="light" aria-label="Claro">
              <LightModeIcon fontSize="small" sx={{ mr: 0.75 }} />
              Claro
            </ToggleButton>
            <ToggleButton value="dark" aria-label="Oscuro">
              <DarkModeIcon fontSize="small" sx={{ mr: 0.75 }} />
              Oscuro
            </ToggleButton>
          </ToggleButtonGroup>
          {colorSchemeMode === 'auto' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
              La aplicación sigue la preferencia de tu sistema operativo.
            </Typography>
          )}
        </Box>

        {/* Renderizar por categorías */}
        {Object.entries(themeCategories).map(([categoryKey, categoryInfo]) => {
          const themesInCategory = getThemesByCategory(categoryKey);
          
          if (themesInCategory.length === 0) return null;

          return (
            <Box key={categoryKey} sx={{ mb: 4 }}>
              {/* Header de categoría */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                {categoryIcons[categoryKey]}
                <Typography variant="h6" fontWeight={600}>
                  {categoryInfo.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ({themesInCategory.length} tema{themesInCategory.length !== 1 ? 's' : ''})
                </Typography>
              </Stack>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {categoryInfo.description}
              </Typography>

              {/* Grid de temas */}
              <Grid container spacing={2}>
                {themesInCategory.map((theme) => (
                  <Grid item xs={12} sm={6} md={4} key={theme.id}>
                    {renderThemeCard(theme)}
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}

        {/* Información adicional */}
        <Box sx={{ mt: 4, p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            💡 <strong>Tip:</strong> Los temas se aplican instantáneamente a toda la aplicación, 
            incluyendo el chat, editor de agentes, editor de flujos y configuración.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
        <Button onClick={onClose} variant="contained">
          Aplicado
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ThemeSelector;
